import { TFunction } from 'i18next';
import { Job, JobQuote } from '../types/job';
import { ChildQuote, Quote, QuoteJob } from '../types/quote';
import { formatDateLocalized } from './dateUtils';
import { HubContext } from '../types/hub';
import { toTorontoDateValue } from '../components/jobs/jobCreation/jobCreationDates';

type RecruiterDisplayInput = Pick<
  Job,
  | 'client'
  | 'is_shadow_recruiter'
  | 'recruiter_display_name'
  | 'recruiter_display_context'
  | 'shadow_recruiter_company_name'
>;

export function getJobRecruiterDisplay(job: RecruiterDisplayInput): {
  name: string;
  context: string | null;
  companyName: string | null;
  avatarUrl: string | null;
} {
  if (job.is_shadow_recruiter) {
    return {
      name: job.recruiter_display_name?.trim() || 'Pigeons',
      context: job.recruiter_display_context?.trim() || null,
      companyName: job.shadow_recruiter_company_name?.trim() || null,
      avatarUrl: null,
    };
  }

  const fullName = [job.client.first_name, job.client.last_name].filter(Boolean).join(' ').trim();

  return {
    name: fullName || job.client.company_name || 'Pigeons',
    context: null,
    companyName: job.client.company_name || null,
    avatarUrl: job.client.avatar_url || null,
  };
}

export type JobPublicationKey = 'private' | 'draft' | 'published';

type JobPublicationFields = Pick<Job, 'visibility' | 'published_at'>;

export function getJobPublicationKey(job: JobPublicationFields): JobPublicationKey {
  if (job.visibility === 'private') return 'private';
  if (!job.published_at) return 'draft';
  return 'published';
}

export function getJobPublicationLabel(
  job: JobPublicationFields,
  t: TFunction,
  language: string
): string {
  const key = getJobPublicationKey(job);
  if (key === 'published') {
    return formatDateLocalized(job.published_at!, language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  return t(`jobModeration.publication.${key}`, { ns: 'admin' });
}

/**
 * Returns the appropriate label for a job's listing type (featured, urgent, etc.)
 * @param job - The job object (Job or QuoteJob)
 * @param t - Translation function
 * @returns The listing type label or null if no special listing type
 */
export function getJobListingTypeLabel(job: Job | QuoteJob, t: TFunction): string | null {
  // QuoteJob doesn't have listing_type, so return null
  if (!('listing_type' in job)) {
    return null;
  }

  if (job.listing_type?.code === 'featured') {
    return t('job_listing_types.featured', { ns: 'common' });
  }

  if (job.listing_type?.code === 'urgent') {
    return t('job_listing_types.urgent', { ns: 'common' });
  }

  return null;
}

/**
 * Returns the expiration date info for a job: localized label, formatted value,
 * and whether the job is already past its expiration.
 *
 * Returns null if expires_at is not set so callers can hide the field entirely.
 */
export function getJobExpirationInfo(
  job: { expires_at?: string | null },
  t: TFunction,
  language: string
): { label: string; value: string; expired: boolean } | null {
  if (!job.expires_at) return null;

  const expiresDate = new Date(job.expires_at);
  if (Number.isNaN(expiresDate.getTime())) return null;

  const expired = expiresDate.getTime() <= Date.now();

  return {
    label: expired ? t('expired_at', { ns: 'jobs' }) : t('expires_at', { ns: 'jobs' }),
    value: formatDateLocalized(job.expires_at, language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    expired,
  };
}

/**
 * Returns the selection date info for a job: localized label and formatted
 * value (the date from which the recruiter starts reviewing quotes).
 *
 * Returns null if selection_date is not set so callers can hide the field.
 */
export function getJobSelectionInfo(
  job: { selection_date?: string | null },
  t: TFunction,
  language: string
): { label: string; value: string } | null {
  if (!job.selection_date) return null;

  const selectionDate = new Date(job.selection_date);
  if (Number.isNaN(selectionDate.getTime())) return null;

  return {
    label: t('selection_date', { ns: 'jobs' }),
    value: formatDateLocalized(job.selection_date, language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
  };
}

export type JobLifecycleState = 'active' | 'expiring_soon' | 'expired' | 'removed';

const JOB_RENEWAL_WINDOW_DAYS = 30;
const JOB_EXPIRING_SOON_DAYS = 4;
const DAY_MS = 24 * 60 * 60 * 1000;

export function getJobLifecycleState(job: {
  expires_at?: string | null;
}): JobLifecycleState | null {
  if (!job.expires_at) return null;
  const expiresAt = new Date(job.expires_at).getTime();
  if (Number.isNaN(expiresAt)) return null;
  const now = Date.now();
  if (now >= expiresAt + JOB_RENEWAL_WINDOW_DAYS * DAY_MS) return 'removed';
  if (now >= expiresAt) return 'expired';
  if (now >= expiresAt - JOB_EXPIRING_SOON_DAYS * DAY_MS) return 'expiring_soon';
  return 'active';
}

// Renewal is free and allowed while expiring-soon or expired, but blocked once removed.
// Open or expired jobs can be renewed -- the renew_job RPC supports both (an expired job
// hasn't been removed yet, so renewing it is what brings it back to 'open'). Any other
// status is rejected because public search and quote submission require status = 'open',
// so renewing e.g. a paused job would leave it unavailable while telling applicants it is
// live again.
export function canRenewJob(job: { expires_at?: string | null; status?: string | null }): boolean {
  if (job.status != null && job.status !== 'open' && job.status !== 'expired') return false;
  const state = getJobLifecycleState(job);
  return state === 'expiring_soon' || state === 'expired';
}

const JOB_RENEWAL_EXTENSION_DAYS = 14;

// Mirrors renew_job's start_date guard (JOB_START_DATE_PASSED /
// EXPIRES_AT_AFTER_START_DATE in
// 20260824000000_reconcile_lifecycle_and_quote_fixes.sql): renewal always
// pushes expires_at 14 days out with no regard for start_date, so once the
// job's start date has passed -- or the new 14-day-out deadline would land
// after it -- the RPC rejects the renewal outright. Compared as Toronto
// calendar days, same as the RPC, so a browser west or east of Toronto still
// agrees with it about which day "today" is.
export function isJobRenewable(job: {
  expires_at?: string | null;
  status?: string | null;
  start_date?: string | null;
}): boolean {
  if (!canRenewJob(job)) return false;
  if (!job.start_date) return true;

  const today = toTorontoDateValue(new Date());
  if (job.start_date < today) return false;

  const newExpiresAt = toTorontoDateValue(
    new Date(Date.now() + JOB_RENEWAL_EXTENSION_DAYS * DAY_MS)
  );
  return newExpiresAt <= job.start_date;
}

// A paused job is invisible to the lifecycle cron (it only ever scans
// open/expired/reviewing_quotes jobs -- see
// internal.process_job_expiration_lifecycle), so its quote/selection window
// and 30-day removal window can lapse while it sits paused with no status
// change. Mirrors the open_job RPC's own atomic window check so the Reopen
// action disappears once reopening would just raise
// JOB_PAST_DEADLINE_CANNOT_REOPEN / JOB_REMOVED_CANNOT_REOPEN instead of
// reviving the job into an already-broken, non-submittable 'open' state.
export function isJobReopenable(job: {
  status?: string | null;
  expires_at?: string | null;
  selection_date?: string | null;
}): boolean {
  if (job.status !== 'paused') return false;

  const now = Date.now();

  if (job.expires_at) {
    const expiresAt = new Date(job.expires_at).getTime();
    if (!Number.isNaN(expiresAt) && now >= expiresAt) return false;
  }

  if (job.selection_date) {
    const selectionDate = new Date(job.selection_date).getTime();
    if (!Number.isNaN(selectionDate) && now >= selectionDate) return false;
  }

  return true;
}

// Reviewing quotes only makes sense once at least one quote has been submitted --
// otherwise start_reviewing_job would move the job into 'reviewing_quotes' with
// nothing to review.
export function isJobReviewable(job: {
  visibility?: string | null;
  published_at?: string | null;
  status?: string | null;
  quotes?: { length: number } | null;
}): boolean {
  return Boolean(
    job.visibility === 'public' &&
    job.published_at &&
    (job.status === 'open' || job.status === 'expired') &&
    (job.quotes?.length ?? 0) > 0
  );
}

/**
 * Returns the published date information for a job including label and formatted date
 * @param job - The job object (Job or QuoteJob)
 * @param t - Translation function
 * @param language - The current language for date formatting
 * @returns Object containing publishedAtLabel and publishedDate
 */
export function getJobPublishedInfo(
  job: Job | QuoteJob,
  t: TFunction,
  language: string
): { publishedAtLabel: string; publishedDate: string | null } {
  const publishedAtLabel =
    'visibility' in job && job.visibility === 'private'
      ? t('requested_at', { ns: 'jobs' })
      : t('published_at', { ns: 'jobs' });

  const publishedDate =
    'visibility' in job && job.visibility === 'private'
      ? 'created_at' in job && job.created_at
        ? formatDateLocalized(job.created_at, language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : null
      : job.published_at
        ? formatDateLocalized(job.published_at, language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : null;

  return { publishedAtLabel, publishedDate };
}

/**
 * Computes the total amount for a quote including child quotes from alliances.
 * @param quote - The quote object
 * @returns The total amount including child quotes
 */
export function computeQuoteTotalAmount(quote: Quote | ChildQuote | JobQuote): number {
  // Calculate main quote amount from tasks
  const mainQuoteAmount = quote.tasks.reduce((sum, task) => sum + task.total_amount, 0);

  // Calculate child quotes amounts from alliances
  const childQuotesAmount =
    'alliances' in quote
      ? quote.alliances.reduce((sum: number, alliance) => {
          if (alliance.child_quote) {
            const childQuoteAmount = alliance.child_quote.tasks.reduce(
              (childSum: number, task) => childSum + task.total_amount,
              0
            );
            return sum + childQuoteAmount;
          }
          return sum;
        }, 0)
      : 0;

  return mainQuoteAmount + childQuotesAmount;
}

/**
 * Computes the amount for a quote excluding child quotes from alliances.
 * @param quote - The quote object
 * @returns The quote amount excluding child quotes
 */
export function computeQuoteAmount(quote: Quote | ChildQuote | JobQuote): number {
  // Calculate only main quote amount from tasks
  return quote.tasks.reduce((sum, task) => sum + task.total_amount, 0);
}

/**
 * Returns the appropriate label for the submissions count based on job visibility and quote request status.
 * @param job - The job object
 * @param t - Translation function
 * @param quotesCount - Number of submissions (optional, defaults to 0)
 * @returns The label to display
 */
export function getQuotesCountLabel(job: Job, t: TFunction): string {
  // If job status is in_progress, show "Quote accepted"

  const quotesCount = 'quotes' in job ? job.quotes?.length || 0 : 0;
  const rejectedQuotesCount =
    'quotes' in job ? job.quotes?.filter(q => q.status === 'rejected').length || 0 : 0;

  if (job.status === 'in_progress') {
    return t('quotes_count_labels.quote_accepted', { ns: 'jobs' });
  }

  // If job visibility is public
  if (job.visibility === 'public') {
    if (!job.published_at) {
      return t('quotes_count_labels.public_draft', { ns: 'jobs' });
    }
    return t('quotes_count_labels.quote_counts', { count: quotesCount, ns: 'jobs' });
  }

  if (job.visibility === 'private') {
    if (rejectedQuotesCount > 0) {
      return t('quotes_count_labels.quote_rejected', { ns: 'jobs' });
    }
    if (job.quote_request_status === 'pending') {
      return t('quotes_count_labels.private_pending', { ns: 'jobs' });
    }
    if (job.quote_request_status === 'accepted') {
      return t('quotes_count_labels.quote_counts', { count: quotesCount, ns: 'jobs' });
    }
    if (job.quote_request_status === 'rejected') {
      return t('quotes_count_labels.private_rejected', { ns: 'jobs' });
    }
    return t('quotes_count_labels.private_pending', { ns: 'jobs' });
  }

  return t('quotes_count_labels.quote_counts', { ns: 'jobs' });
}

// for one HubContext

export function getQuotesCountLabelForHub(hub: HubContext, t: TFunction): string {
  // If job status is in_progress, show "Quote accepted"

  const quotesCount = hub.quotes?.length || 0;
  const rejectedQuotesCount = hub.quotes?.filter(q => q.status === 'rejected').length || 0;

  const quoteRequestStatus = hub.job.quote_requests.some(q => q.status === 'pending')
    ? 'pending'
    : hub.job.quote_requests.some(q => q.status === 'accepted')
      ? 'accepted'
      : hub.job.quote_requests.some(q => q.status === 'rejected')
        ? 'rejected'
        : null;

  if (hub.job.status === 'in_progress') {
    return t('quotes_count_labels.quote_accepted', { ns: 'jobs' });
  }

  // If job visibility is public
  if (hub.job.visibility === 'public') {
    if (!hub.job.published_at) {
      return t('quotes_count_labels.public_draft', { ns: 'jobs' });
    }
    return t('quotes_count_labels.quote_counts', { count: quotesCount, ns: 'jobs' });
  }

  if (hub.job.visibility === 'private') {
    if (rejectedQuotesCount > 0) {
      return t('quotes_count_labels.quote_rejected', { ns: 'jobs' });
    }
    if (quoteRequestStatus === 'pending') {
      return t('quotes_count_labels.private_pending', { ns: 'jobs' });
    }
    if (quoteRequestStatus === 'accepted') {
      return t('quotes_count_labels.quote_counts', { count: quotesCount, ns: 'jobs' });
    }
    if (quoteRequestStatus === 'rejected') {
      return t('quotes_count_labels.private_rejected', { ns: 'jobs' });
    }
    return t('quotes_count_labels.private_pending', { ns: 'jobs' });
  }

  return t('quotes_count_labels.quote_counts', { ns: 'jobs' });
}
